import React, { useState, useEffect } from 'react';
import { IoHomeOutline } from 'react-icons/io5';
import { FiUser, FiLock, FiX, FiBriefcase } from 'react-icons/fi';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BASE_URL, LOGIN } from '../../utils/config';
import { setWebUserInfo, initializeWebUserInfo } from '../../utils/webConfig';

// Animated loader component
const Spinner = () => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading...</p>
    </div>
  </div>
);

const Dashboard = () => {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    // Check if token already exists
    const token = localStorage.getItem('accessToken');
    if (!token) {
      // Show modal if no token exists
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, []);

  useEffect(() => {
    initializeWebUserInfo();
  }, []);

  const isElectron = () => {
    return window && window.process && window.process.type;
  };

  let ipcRenderer;
  if (isElectron()) {
    ipcRenderer = window.require('electron').ipcRenderer;
  }

  const handleApiLogin = async (e) => {
    e.preventDefault();
    
    if (!userId || !password) {
      toast.error('Please fill all fields', {
        autoClose: 2000,
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${LOGIN}`, {
        headers: {
          'TenantName': 'ABC HEALTH CARE',
          'UserCode': userId.toUpperCase(),
          'Password': password,
          'XApiKey': process.env.REACT_APP_API_KEY || '',
        },
      });

      if (response.status === 200) {
        const accessToken = response.data.data._accessToken;
        const refreshToken = response.data.data._refreshToken || '';
        const clientInfo = response.data.data.clientInfo?.[0] || {};

        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);

        if (isElectron()) {
          await ipcRenderer.invoke('store-set', 'clientInfo', clientInfo);
        } else {
          setWebUserInfo({ clientInfo });
        }

        toast.success('Authentication successful!', {
          autoClose: 2000,
        });
        // Close modal after successful login
        setShowModal(false);
      } else {
        toast.error('Invalid credentials', {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.message || 'Authentication failed. Please try again.', {
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Authentication Modal - Render outside main content */}
      {showModal && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4" 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0,
            zIndex: 9999
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>

          <div 
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 animate-fade-in-up" 
            style={{ 
              zIndex: 10000, 
              position: 'relative',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            <button
              onClick={() => {
                // Don't allow closing if no token exists
                const token = localStorage.getItem('accessToken');
                if (!token) {
                  toast.warning('Please authenticate to continue', {
                    autoClose: 2000,
                  });
                  return;
                }
                setShowModal(false);
              }}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-10"
            >
              <FiX size={20} className="text-gray-500" />
            </button>

            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Authenticate</h2>
              <p className="text-gray-500 text-sm">Enter your firm credentials</p>
            </div>

            <form onSubmit={handleApiLogin} className="space-y-6">

              <div>
                <label htmlFor="userId" className="block text-sm font-medium text-gray-700 mb-2">
                  User ID
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <FiUser className="text-gray-400" size={20} />
                  </div>
                  <input
                    type="text"
                    id="userId"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value.toUpperCase())}
                    required
                    placeholder="Enter user ID"
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <FiLock className="text-gray-400" size={20} />
                  </div>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Enter password"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleApiLogin(e);
                      }
                    }}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={!userId || !password || loading}
                className={`w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-[1.02] shadow-lg ${
                  (!userId || !password || loading) ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {loading ? 'Authenticating...' : 'Authenticate'}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="p-6 w-full">
        <ToastContainer
        position="top-center"
        autoClose={2000}
        limit={1}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />

      {loading && <Spinner />}

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
          <IoHomeOutline size={32} className="text-blue-600" />
          Dashboard
        </h1>
        <p className="text-gray-500 mt-2">Welcome to Fluxion Console</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">System Overview</h3>
          <p className="text-gray-500 text-sm">Monitor your system status and performance</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Quick Actions</h3>
          <p className="text-gray-500 text-sm">Access frequently used features</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Recent Activity</h3>
          <p className="text-gray-500 text-sm">View your recent operations</p>
        </div>
      </div>

      {/* Animation Styles */}
      <style>
        {`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.3s ease-out forwards;
        }
        `}
      </style>
      </div>
    </>
  );
};

export default Dashboard;
